import { Layout, Template } from '@dtx-company/flow-codegen/src/code/generated.types'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useIsMobile } from '@dtx-company/true-common/src/hooks/useIsMobile'
import { usePrintStoreTemplatesQuery } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreQueries'
import { useRouter } from 'next/router'
import { validate as validateUUID } from 'uuid'
import events from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'

export interface UseTemplatesListState {
  handleLoadMore: () => void
  clearTemplates: () => void
  isLoading: boolean
  templates: Template[] | undefined
  hasMore: boolean
  isFetching: boolean
  hasTemplates: boolean
  hasPredesignedTemplates: boolean
  predesignedTemplatesLength: number
}

export const useTemplatesListState = (props: {
  predesignedFetchLimit: number
}): UseTemplatesListState => {
  const isMobile = useIsMobile()
  const [paginationAfter, setPaginationAfter] = useState<string>('')
  const [predesignedTemplates, setPredesignedTemplates] = useState<Template[] | undefined>(
    undefined
  )
  const router = useRouter()
  const { productId, templateCategoryId } = router.query

  const verticalSelectedCategories = useCodeSelector(
    (state: RootCodeState) => state.printStore.selectedCategories['verticals']
  )
  const useCaseSelectedCategories = useCodeSelector(
    (state: RootCodeState) => state.printStore.selectedCategories['useCases']
  )

  const verticalSelectedCategoryIds = useMemo<string[]>(() => {
    return verticalSelectedCategories.map(v => v.categoryId)
  }, [verticalSelectedCategories])
  const useCaseSelectedCategoryIds = useMemo<string[]>(() => {
    return useCaseSelectedCategories.map(v => v.categoryId)
  }, [useCaseSelectedCategories])

  const commonTemplateQueryArgs = {
    productId,
    categoryIds: [...verticalSelectedCategoryIds, ...useCaseSelectedCategoryIds]
  }

  const isValidUUID = productId && validateUUID(productId as string)

  /**
   * Fetch blank templates separately from predesigned templates until pagination with orderBy is supported
   * by the backend. There will only ever be a couple of blank templates per product, so we do not need to
   * handle pagination for them.
   */
  const { blankTemplates, isBlankTemplatesLoading, isBlankTemplatesFetching } =
    usePrintStoreTemplatesQuery(
      {
        ...commonTemplateQueryArgs,
        layouts: [Layout.Blank],
        limit: 5,
        after: ''
      },
      {
        skip: isMobile || !props.predesignedFetchLimit || !isValidUUID,
        selectFromResult: ({ data, isLoading, isFetching }) => ({
          blankTemplates: data?.getTemplates?.templates || [],
          isBlankTemplatesLoading: isLoading,
          isBlankTemplatesFetching: isFetching
        })
      }
    )

  /**
   * Fetch predesigned templates separately from blank templates until pagination with orderBy is supported
   * by the backend. Pagination is necessary.
   */
  const {
    predesignedTemplatesList,
    pageInfo,
    isPredesignedTemplatesLoading,
    isPredesignedTemplatesFetching
  } = usePrintStoreTemplatesQuery(
    {
      ...commonTemplateQueryArgs,
      layouts: [Layout.Predesigned],
      limit: props.predesignedFetchLimit,
      after: paginationAfter
    },
    {
      skip: !props.predesignedFetchLimit || !isValidUUID,
      selectFromResult: ({ data, isLoading, isFetching }) => ({
        predesignedTemplatesList: data?.getTemplates?.templates || [],
        pageInfo: data?.getTemplates?.pageInfo,
        isPredesignedTemplatesLoading: isLoading,
        isPredesignedTemplatesFetching: isFetching
      })
    }
  )

  useEffect(() => {
    if (predesignedTemplatesList.length) {
      setPredesignedTemplates((prev = []) => {
        const tempCombinedTemplates = prev?.concat(predesignedTemplatesList)
        // filter to prevent duplicate concats
        const filteredCombinedTemplates = tempCombinedTemplates?.filter(
          (value, i, currFilterArray) =>
            currFilterArray.findIndex(tempValue => tempValue.templateId === value.templateId) === i
        )
        return filteredCombinedTemplates
      })
    }
  }, [predesignedTemplatesList])

  const handleLoadMore = useCallback(() => {
    if (pageInfo?.hasNextPage) {
      setPaginationAfter(pageInfo?.endCursor ?? '')
    }
    events.Templates_Clicked_Load_More_Button({
      productId: productId ? String(productId) : '',
      templateCategoryId: templateCategoryId ? String(templateCategoryId) : ''
    })
  }, [pageInfo, productId, templateCategoryId])

  const templates = useMemo(() => {
    return isMobile
      ? predesignedTemplates
      : (blankTemplates ?? []).concat(predesignedTemplates ?? [])
  }, [blankTemplates, isMobile, predesignedTemplates])

  const clearTemplates = useCallback((): void => {
    setPredesignedTemplates([])
    setPaginationAfter('')
  }, [])

  return {
    clearTemplates,
    handleLoadMore,
    isLoading: isBlankTemplatesLoading || isPredesignedTemplatesLoading,
    templates,
    hasMore: Boolean(pageInfo?.hasNextPage),
    isFetching: isBlankTemplatesFetching || isPredesignedTemplatesFetching,
    hasTemplates: Boolean(templates?.length),
    hasPredesignedTemplates: Boolean(predesignedTemplates?.length),
    predesignedTemplatesLength: predesignedTemplates?.length ?? 0
  }
}
